import { useCallback, useMemo, useState } from "react";
import { isValidPositiveMoneyValue } from "../../../utilities/is-valid-money";

export type UseCollectCashFormType = {
  defaultValues?: {
    note?: string;
    amount?: string;
  };
  onSubmit?: (values: { note: string; amount: string }) => Promise<void>;
};

export const useCollectCashForm = ({
  defaultValues,
  onSubmit,
}: UseCollectCashFormType = {}) => {
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState(defaultValues?.note || "");
  const [amount, setAmount] = useState(defaultValues?.amount || "0.00");

  const isValid = useMemo(() => {
    return amount == "0.00" || isValidPositiveMoneyValue(amount);
  }, [amount]);

  const setAmountFn = useCallback(
    (value: string) => {
      setAmount(value);
    },
    [setAmount]
  );

  const setNoteFn = useCallback(
    (value: string) => {
      setNote(value);
    },
    [setNote]
  );

  const isDirty = useMemo(() => {
    return amount !== "0.00";
  }, [amount]);

  const handleOnSubmit = useCallback(async () => {
    if (isValid) {
      if (onSubmit) {
        setSubmitting(true);
        try {
          await onSubmit({ note, amount });
          setSubmitting(false);
        } catch (err) {
          setSubmitting(false);
          throw err;
        }
      }
    }
  }, [onSubmit, isValid]);

  return {
    note,
    amount,
    isValid,
    isDirty,
    setAmount: setAmountFn,
    setNote: setNoteFn,
    submitting,
    handleOnSubmit,
  };
};
